// NowPane — "This month" tab of InputDrawerV3.
// Shows stage-cards (Upload / Structure / Cross-link) and a chronological
// timeline of pipeline events for the current month.

import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation, Trans } from "react-i18next";
import { useSelector } from "react-redux";

import CheckCircle from "@material-ui/icons/CheckCircle";
import Warning from "@material-ui/icons/Warning";
import Error from "@material-ui/icons/Error";
import ErrorOutline from "@material-ui/icons/ErrorOutline";
import HourglassEmpty from "@material-ui/icons/HourglassEmpty";
import CloudUpload from "@material-ui/icons/CloudUpload";
import CompareArrows from "@material-ui/icons/CompareArrows";
import Lock from "@material-ui/icons/Lock";
import Autorenew from "@material-ui/icons/Autorenew";
import Flag from "@material-ui/icons/Flag";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import Check from "@material-ui/icons/Check";
import Clear from "@material-ui/icons/Clear";
import GetApp from "@material-ui/icons/GetApp";
import ArrowDropDown from "@material-ui/icons/ArrowDropDown";
import CircularProgress from "@material-ui/core/CircularProgress";

import { fileDownloadClient } from "../../../../../services/axiosClient";
import useDrawerStyles from "../styles";
import useThemeColors from "../../../../../hooks/useThemeColors";
import useNativeWheelScroll from "../../hooks/useNativeWheelScroll";
import { useOverlayScrollStyles } from "../../styles";
import DecisionsCard from "../DecisionsCard";
import DownloadMenu from "../DownloadMenu";
import PaneErrorBanner from "./PaneErrorBanner";
import DrawerPaneLoadingLayout from "../../DrawerPaneLoadingLayout";
import { SEVERITY_KIND, SEVERITY_GROUPS, sevKindFor } from "../../severity";

// --- CrossSampleCopy -------------------------------------------------------
// Discrete button to download (blob) or copy (clipboard) the list of
// unmatched keys for the consultant.
function sampleToCsv(sample) {
  if (!Array.isArray(sample) || sample.length === 0) return "";
  const cols = new Set();
  sample.forEach((row) => {
    if (row && typeof row === "object")
      Object.keys(row).forEach((k) => cols.add(k));
  });
  const colList = Array.from(cols);
  const escape = (v) => {
    if (v == null) return "";
    const s = String(v);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const header = colList.join(",");
  const rows = sample.map((row) =>
    colList.map((c) => escape(row && row[c])).join(","),
  );
  return [header, ...rows].join("\n");
}

const CrossSampleCopy = ({ sample, noteId, hasMissingBlob }) => {
  const classes = useDrawerStyles();
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  const useDownload = Boolean(hasMissingBlob && noteId);

  const onClick = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (useDownload) {
        // fileDownloadClient is pre-configured with JWT and responseType:"blob".
        const resp = await fileDownloadClient.get(
          `api/v3/cross-links/notes/${noteId}/missing.csv`,
        );
        const blob = new Blob([resp.data], { type: "text/csv" });
        const cd = resp.headers["content-disposition"] || "";
        const m = cd.match(/filename="?([^";]+)"?/i);
        const filename = (m && m[1]) || `missing_keys_${noteId}.csv`;
        const objectUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(objectUrl);
      } else {
        const csv = sampleToCsv(sample);
        if (!csv) return;
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(csv);
        } else {
          const el = document.createElement("textarea");
          el.value = csv;
          el.style.position = "fixed";
          el.style.opacity = "0";
          document.body.appendChild(el);
          el.select();
          document.execCommand("copy");
          document.body.removeChild(el);
        }
      }
    } catch (_) {
      // Silent failure — button returns to neutral state.
    } finally {
      setBusy(false);
    }
  }, [busy, useDownload, noteId, sample]);

  const hasAnything =
    (Array.isArray(sample) && sample.length > 0) || useDownload;
  if (!hasAnything) return null;

  const label = useDownload
    ? t("modeling_inputs_v3.now_pane.download_missing_keys")
    : t("modeling_inputs_v3.now_pane.copy_missing_keys");
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={classes.tlCrossCopyBtn}
      title={
        useDownload
          ? t("modeling_inputs_v3.now_pane.download_missing_keys_title")
          : t("modeling_inputs_v3.now_pane.copy_missing_keys_title")
      }
    >
      <span>{label}</span>
      {busy && (
        <span
          className={classes.tlCrossCopySpinner}
          aria-label={t("modeling_inputs_v3.now_pane.loading_indicator")}
        />
      )}
    </button>
  );
};
CrossSampleCopy.propTypes = {
  sample: PropTypes.array,
  total: PropTypes.number,
  noteId: PropTypes.string,
  hasMissingBlob: PropTypes.bool,
};

// --- MarkAnalyzedControl ---------------------------------------------------
// Botao "Marcar como analisado" (+ carimbo "analisado por X em [data]" e
// acao "Desmarcar") renderizado SOMENTE em sub-cards de warning (sev=2).
// Toggle pelo endpoint V3 ``mark-verified`` (via prop ``onMark``). A nota
// segue amarela na timeline; o resto da UI deixa de registrar o aviso.
const MarkAnalyzedControl = ({
  sub,
  override,
  onMark,
  pending,
  withDivider,
}) => {
  const classes = useDrawerStyles();
  const { t } = useTranslation();
  if (
    !sub ||
    sub.severity !== 2 ||
    !sub.noteId ||
    typeof onMark !== "function"
  ) {
    return null;
  }
  // Override otimista (pos-POST) tem prioridade sobre o dado do servidor
  // ate o refetch consolidado convergir.
  const markedVerified = override
    ? override.markedVerified
    : sub.markedVerified;
  const markedBy = override ? override.markedBy : sub.markedBy;
  const markedAt = override ? override.markedAt : sub.markedAt;
  if (markedVerified) {
    const who =
      markedBy || t("modeling_inputs_v3.now_pane.timeline.someone_fallback");
    return (
      <div className={classes.tlCrossDecision}>
        <Check
          className={`${classes.tlCrossDecisionIcon} ${classes.tlCrossDecisionIconUp}`}
        />
        <span>
          <Trans
            i18nKey="modeling_inputs_v3.now_pane.timeline.marked_as_analyzed_by"
            values={{ who }}
            components={{ 1: <strong /> }}
          />
          {markedAt && (
            <>
              {" "}
              {t("modeling_inputs_v3.now_pane.timeline.decision_at")}{" "}
              {formatDeliveredAt(markedAt)}
            </>
          )}{" "}
          <button
            type="button"
            className={classes.tlMarkUndoBtn}
            disabled={pending}
            onClick={() => onMark(sub.noteId, false)}
          >
            {pending
              ? t("modeling_inputs_v3.now_pane.timeline.unmarking")
              : t("modeling_inputs_v3.now_pane.timeline.mark_undo")}
          </button>
        </span>
      </div>
    );
  }
  return (
    <>
      {/* Divisor "|" quando o botao fica inline ao lado de outra acao
          (ex.: "Baixar chaves nao cruzadas" nos cruzamentos), para nao
          aparecerem colados. So renderiza quando ha acao a esquerda. */}
      {withDivider && (
        <span className={classes.tlActionDivider} aria-hidden="true">
          |
        </span>
      )}
      <button
        type="button"
        className={classes.tlCrossCopyBtn}
        disabled={pending}
        onClick={() => onMark(sub.noteId, true)}
        title={t("modeling_inputs_v3.now_pane.timeline.mark_as_analyzed_title")}
      >
        <span>
          {pending
            ? t("modeling_inputs_v3.now_pane.timeline.marking")
            : t("modeling_inputs_v3.now_pane.timeline.mark_as_analyzed")}
        </span>
      </button>
    </>
  );
};

MarkAnalyzedControl.propTypes = {
  sub: PropTypes.object,
  override: PropTypes.object,
  onMark: PropTypes.func,
  pending: PropTypes.bool,
  withDivider: PropTypes.bool,
};

// --- i18n helper -----------------------------------------------------------

const I18N_LANG_KEYS = ["pt", "en", "es"];

function isI18nObject(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    I18N_LANG_KEYS.some((key) =>
      Object.prototype.hasOwnProperty.call(value, key),
    )
  );
}

function parseMaybeJson(value) {
  if (
    typeof value === "string" &&
    (value.startsWith("{") || value.startsWith("["))
  ) {
    try {
      return JSON.parse(value);
    } catch (_) {
      return value;
    }
  }
  return value;
}

// ``check_description``/``notes`` no V2/V3 podem vir como string, como
// objeto i18n já parseado pelo backend (``{en, pt, es}``), ou como string
// JSON (caso o parser não tenha reconhecido). Objetos que NÃO são i18n
// (ex.: metadados ``{year, month, rows_before}`` emitidos pelo
// ``generic_structure_check_v3``) retornam string vazia — o chamador
// deve usar ``check_description`` ou formatar o payload auxiliar.
function renderI18n(value, lang) {
  if (value == null || value === "") return "";
  if (typeof value === "string") {
    if (value.startsWith("{") || value.startsWith("[")) {
      try {
        return renderI18n(JSON.parse(value), lang);
      } catch (_) {
        return value;
      }
    }
    return value;
  }
  if (typeof value !== "object") return String(value);
  if (!isI18nObject(value)) return "";
  const short = (lang || "pt").slice(0, 2);
  if (value[short]) return value[short];
  if (value.pt) return value.pt;
  if (value.en) return value.en;
  if (value.es) return value.es;
  return "";
}

function formatStructureNoteBody(note, lang, t) {
  const descRaw = parseMaybeJson(note.check_description);
  const desc =
    renderI18n(descRaw, lang) || (typeof descRaw === "string" ? descRaw : "");

  const notesRaw = parseMaybeJson(note.notes);
  const notesText =
    typeof notesRaw === "string" && !notesRaw.startsWith("{")
      ? notesRaw
      : renderI18n(notesRaw, lang);
  if (notesText) return notesText;

  if (notesRaw && typeof notesRaw === "object" && !Array.isArray(notesRaw)) {
    const metaParts = [];
    if (notesRaw.year != null && notesRaw.month != null) {
      metaParts.push(
        t("modeling_inputs_v3.now_pane.timeline.filter_period_meta", {
          month: notesRaw.month,
          year: notesRaw.year,
        }),
      );
    }
    if (notesRaw.rows_before != null && notesRaw.rows_after != null) {
      metaParts.push(
        t("modeling_inputs_v3.now_pane.timeline.filter_rows_meta", {
          before: notesRaw.rows_before,
          after: notesRaw.rows_after,
        }),
      );
    }
    const metaLine = metaParts.join(" · ");
    if (desc && metaLine) return `${desc}\n${metaLine}`;
    return desc || metaLine;
  }

  return desc;
}

// --- Stage cards -----------------------------------------------------------

function _envioStageFrom({
  dataSource,
  controlState,
  isUploading,
  busyAction,
  isPermanent,
  t,
}) {
  const title = t("modeling_inputs_v3.stages.envio");
  if (busyAction === "reprocess") {
    return {
      id: "envio",
      title,
      state: "ok",
      label: t("modeling_inputs_v3.stages.label_existing_file"),
    };
  }
  if (isUploading || dataSource === "processing" || dataSource === "running") {
    return {
      id: "envio",
      title,
      state: "run",
      label: t("modeling_inputs_v3.stages.label_in_progress"),
    };
  }
  if (dataSource === "error" || dataSource === "system_error") {
    return {
      id: "envio",
      title,
      state: "err",
      label: t("modeling_inputs_v3.stages.label_upload_failed"),
    };
  }
  const noControl = !controlState || controlState === "pending";
  if (dataSource === "success" || (!noControl && dataSource !== "waiting")) {
    return {
      id: "envio",
      title,
      state: "ok",
      label: t("modeling_inputs_v3.stages.label_received"),
    };
  }
  // Inputs de natureza permanente (yearly / singleton / carry_forward /
  // monthly+reuse_auto): o backend so marca ``is_permanent_for_month``
  // quando ha cobertura REAL — entrega no mes corrente OU herdada de
  // outro mes do mesmo ano fiscal. Logo o "Envio" ja aconteceu, e
  // mostramos "Recebido" (e nao "Aguardando envio"), inclusive nos
  // meses herdados em que o control do mes corrente esta ``pending``.
  //
  // Antes este caso era curto-circuitado no topo da funcao como
  // "Informacao continua", o que (a) mascarava o fato de que um
  // arquivo foi de fato recebido e (b) escondia estados reais
  // (enviando / erro de parse) de um reenvio no mes corrente. A
  // proveniencia (quem entregou e quando) continua na timeline
  // ("Arquivo recebido por X em <data>"). ``label_permanent`` deixa
  // de ser usado no V3 — fica como fallback semantico caso volte a
  // existir input permanente genuinamente sem arquivo.
  if (isPermanent) {
    return {
      id: "envio",
      title,
      state: "ok",
      label: t("modeling_inputs_v3.stages.label_received"),
    };
  }
  return {
    id: "envio",
    title,
    state: "wait",
    label: t("modeling_inputs_v3.stages.label_waiting_send"),
  };
}

function _crossStageFrom(xlState, t, xlCount) {
  const title = t("modeling_inputs_v3.stages.cross_link");
  // "processing" replaced "running" in stage enum; accept both for compatibility.
  if (xlState === "processing" || xlState === "running") {
    return {
      id: "cross",
      title,
      state: "run",
      label: t("modeling_inputs_v3.stages.label_verifying"),
    };
  }
  if (xlState === "warning") {
    return {
      id: "cross",
      title,
      state: "warn",
      label: t("modeling_inputs_v3.stages.label_attention_points"),
    };
  }
  if (xlState === "error" || xlState === "system_error") {
    return {
      id: "cross",
      title,
      state: "err",
      label: t("modeling_inputs_v3.stages.label_critical_points"),
    };
  }
  if (xlState === "pending_decision") {
    return {
      id: "cross",
      title,
      state: "warn",
      label: t("modeling_inputs_v3.stages.label_open_question"),
    };
  }
  if (xlState === "success") {
    return {
      id: "cross",
      title,
      state: "ok",
      label: t("modeling_inputs_v3.stages.label_verified"),
    };
  }
  if (xlState === "partial") {
    // partial = some xlinks completed clean + others not yet run. "run" kind conveys in-progress without implying done.
    return {
      id: "cross",
      title,
      state: "run",
      label: t("modeling_inputs_v3.stages.label_verifying"),
    };
  }
  if (xlState == null || xlState === "no_xlinks") {
    // Onda 2026-05-17: discriminamos "input nao participa de NENHUM
    // cross-link" (``count = 0`` / null) de "input participa, mas
    // contrapartes ainda nao chegaram" (``count > 0`` e state null,
    // tipico de inputs semi-fixos no mes corrente). Antes ambos
    // caiam em "Sem cruzamentos", que era enganoso no segundo caso.
    //
    // Onda 2026-05-27: quando NAO ha xlinks declarados (count = 0),
    // a etapa "cruzamento" e' irrelevante — nao ha o que validar.
    // Mostramos verde-com-check ("ok") em vez de cinza ("wait"),
    // sinalizando que o pipeline desse input concluiu o necessario.
    // O override e' seguro porque ``buildStages`` so deixa este path
    // bubblar quando a estrutura ja esta validada (success/warning/
    // permanent-noControl); nos demais ramos (processing/error/etc.)
    // a cor do cross e' substituida explicitamente.
    const hasDeclaredXlinks = typeof xlCount === "number" && xlCount > 0;
    if (!hasDeclaredXlinks) {
      return {
        id: "cross",
        title,
        state: "ok",
        label: t("modeling_inputs_v3.stages.label_no_crosslinks"),
      };
    }
    return {
      id: "cross",
      title,
      state: "wait",
      label: t("modeling_inputs_v3.stages.label_waiting"),
    };
  }
  return {
    id: "cross",
    title,
    state: "wait",
    label: t("modeling_inputs_v3.stages.label_waiting"),
  };
}

function buildStages({
  controlState,
  isUploading,
  busyAction,
  hasNotesError,
  xlState,
  xlCount,
  dataSource,
  isPermanent,
  isWaived,
  attentionLabel,
  t,
}) {
  const structTitle = t("modeling_inputs_v3.stages.structure");
  const crossTitle = t("modeling_inputs_v3.stages.cross_link");

  // Waived: no pipeline to run — all three stages reflect "not applicable"
  // to avoid confusion with genuine "waiting" (same gray color).
  if (isWaived) {
    return [
      {
        id: "envio",
        title: t("modeling_inputs_v3.stages.envio"),
        state: "ok",
        label: t("modeling_inputs_v3.stages.label_completed_no_file"),
      },
      {
        id: "struct",
        title: structTitle,
        state: "wait",
        label: t("modeling_inputs_v3.stages.label_not_applicable"),
      },
      {
        id: "cross",
        title: crossTitle,
        state: "wait",
        label: t("modeling_inputs_v3.stages.label_not_applicable"),
      },
    ];
  }

  // "pending" controlState means the backend bootstrapped the record but nothing
  // was delivered yet — treat as "no control" for stage-card purposes.
  const noControl = !controlState || controlState === "pending";
  const envio = _envioStageFrom({
    dataSource,
    controlState,
    isUploading,
    busyAction,
    isPermanent,
    t,
  });
  const cross = _crossStageFrom(xlState, t, xlCount);

  if (busyAction === "reprocess") {
    return [
      envio,
      {
        id: "struct",
        title: structTitle,
        state: "run",
        label: t("modeling_inputs_v3.stages.label_reprocessing"),
      },
      {
        id: "cross",
        title: crossTitle,
        state: "wait",
        label: t("modeling_inputs_v3.stages.label_will_recalculate"),
      },
    ];
  }

  // Permanent input without a delivery this month: o ENVIO e a
  // ESTRUTURA herdam do envio "mae" feito no periodo de origem
  // (calendar card ja' verde), por isso ficam "validada anteriormente".
  //
  // Cruzamento, no entanto, e' SEMPRE do periodo corrente — um
  // tarifario semi-fixo entregue em jan pode ser cruzado com a
  // venda bruta de abr, e esse cruzamento eh um evento de abr/2026,
  // nao de jan/2026. Forcar "validada anteriormente" no cross para
  // todo semi-fixo era enganoso em dois cenarios:
  //
  //   1) input sem nenhum cross-link declarado -> texto deveria ser
  //      "Sem cruzamentos" (xlState=null/no_xlinks), nao "validada
  //      anteriormente";
  //   2) input com cross-link no mes mas contraparte ainda nao
  //      enviada -> texto deveria ser "Aguardando", nao "validada
  //      anteriormente".
  //
  // Onda 2026-05-17: deixamos ``_crossStageFrom`` (i.e. ``cross``)
  // ditar o copy do cruzamento, identico ao comportamento de
  // inputs mensais. A unica especializacao do semi-fixo agora e' o
  // envio + estrutura.
  if (isPermanent && noControl && !isUploading) {
    return [
      envio,
      {
        id: "struct",
        title: structTitle,
        state: "ok",
        label: t("modeling_inputs_v3.stages.label_previously_validated"),
      },
      cross,
    ];
  }

  if (noControl && !isUploading) {
    return [
      envio,
      {
        id: "struct",
        title: structTitle,
        state: "wait",
        label: t("modeling_inputs_v3.stages.label_waiting"),
      },
      cross,
    ];
  }

  if (
    isUploading ||
    controlState === "uploaded" ||
    controlState === "processing"
  ) {
    return [
      envio,
      {
        id: "struct",
        title: structTitle,
        state: "run",
        label: t("modeling_inputs_v3.stages.label_reading"),
      },
      // Cross-linking doesn't run until structure finishes reading.
      {
        id: "cross",
        title: crossTitle,
        state: "wait",
        label: t("modeling_inputs_v3.stages.label_waiting"),
      },
    ];
  }

  if (controlState === "system_error") {
    return [
      envio,
      {
        id: "struct",
        title: structTitle,
        state: "err",
        label: t("modeling_inputs_v3.stages.label_processing_error"),
      },
      {
        id: "cross",
        title: crossTitle,
        state: "wait",
        label: t("modeling_inputs_v3.stages.label_waiting_integration"),
      },
    ];
  }

  // Onda 3.4 (CCU Testes): ``rejected_by_xlink`` — controlState e'
  // ``error`` mas a estrutura foi aceita pelo parser. O arquivo foi
  // rejeitado pela decisao D-22 de cruzamento. Mostra "Arquivo
  // rejeitado" na caixa CRUZAMENTO em vez de ESTRUTURA.
  if (attentionLabel === "rejected_by_xlink") {
    return [
      envio,
      {
        id: "struct",
        title: structTitle,
        state: "ok",
        label: t("modeling_inputs_v3.stages.label_read_ok"),
      },
      {
        id: "cross",
        title: crossTitle,
        state: "err",
        label: t("modeling_inputs_v3.stages.label_rejected"),
      },
    ];
  }

  if (controlState === "error" || hasNotesError) {
    return [
      envio,
      {
        id: "struct",
        title: structTitle,
        state: "err",
        label: t("modeling_inputs_v3.stages.label_rejected"),
      },
      {
        id: "cross",
        title: crossTitle,
        state: "wait",
        label: t("modeling_inputs_v3.stages.label_waiting_correction"),
      },
    ];
  }

  if (controlState === "warning") {
    return [
      envio,
      {
        id: "struct",
        title: structTitle,
        state: "warn",
        label: t("modeling_inputs_v3.stages.label_read_with_attention"),
      },
      cross,
    ];
  }

  // success
  return [
    envio,
    {
      id: "struct",
      title: structTitle,
      state: "ok",
      label: t("modeling_inputs_v3.stages.label_read_ok"),
    },
    cross,
  ];
}

const STAGE_ICON = {
  ok: CheckCircle,
  warn: Warning,
  err: Error,
  wait: HourglassEmpty,
  run: Autorenew,
};

function StageCard({ stage, classes }) {
  const Icon = STAGE_ICON[stage.state] || HourglassEmpty;
  const stateClass =
    stage.state === "ok"
      ? classes.stageCardOk
      : stage.state === "warn"
        ? classes.stageCardWarn
        : stage.state === "err"
          ? classes.stageCardErr
          : stage.state === "run"
            ? classes.stageCardRunning
            : classes.stageCardWait;

  return (
    <div className={`${classes.stageCard} ${stateClass}`}>
      <div className={classes.stageTitle}>{stage.title}</div>
      <div className={classes.stageVal}>
        <Icon />
        {stage.label}
      </div>
    </div>
  );
}

StageCard.propTypes = {
  stage: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

// --- Timeline construction -------------------------------------------------

// Mapa severity → (kind para styling + ícone).
//
// ``note`` (sev=1) e ``info`` (marcos cronologicos neutros como
// "Periodo aberto") sao KINDS DIFERENTES. Ambos tinham o mesmo
// estilo cinza ate a introducao do ``tlItemNote`` azul — separar
// permite destacar observacoes acionaveis do consultor sem gritar
// como um warning de sev=2.
// SEVERITY_KIND / SEVERITY_GROUPS vivem agora em
// ``inputs_v3_components/severity.js`` (importado acima). Mantemos a
// referencia documental aqui apenas como ponteiro: qualquer mudanca
// em rotulos / icones / classes CSS de severidade deve ser feita no
// arquivo central, nunca duplicada por arquivo.

function buildTimeline({
  t,
  control,
  notes,
  answeredDecisions,
  isUploading,
  busyAction,
  uploadProgress,
  notesLoading,
  notesError,
  uploaderName,
  isPermanentNature,
  isWaived,
  waivedAt,
  waivedByName,
  lang,
  // ``xlState`` e ``xlCount`` vem do runtime publicado via prop
  // ``input.cross_linking`` (adapter). Permitem distinguir os tres
  // cenarios na timeline: (i) nao ha cross-link declarado, (ii)
  // cross-links rodaram limpos, (iii) cross-links rodaram com
  // apontamentos. Sem essa info, antes ficavamos em silencio quando
  // nao havia notas CSL — o que dava a sensacao de "card vazio".
  xlState,
  xlCount,
  // ``cleanCompletedNames``: lista de display_names dos cross-links
  // que ja rodaram em ``success`` SEM apontamento neste mes. Vem do
  // backend (``cross_linking.clean_completed_names``). Usado para
  // listar (separado por virgula) na timeline o que foi verificado
  // OK quando o agregado e ``partial`` ou ``success``.
  cleanCompletedNames,
  // ``crossLinksByMeta``: Map(meta_name -> {display_name, ...}) com
  // os cross-links do drawer (mesma fonte que a aba "Cruzamentos").
  // Cada nota CSL traz ``cross_link_meta_name`` que serve como
  // chave neste mapa para obter o rotulo legivel.
  crossLinksByMeta,
}) {
  const items = [];
  const hasDelivered = !!(control && control.delivered_at);

  // Rotulo do "time" dos eventos de conclusao da timeline. Quando ha um
  // datetime confiavel da conclusao (parse da estrutura / ultimo
  // cruzamento), exibe "Concluido em 09/05 · 22:01"; senao, o rotulo
  // generico "Concluido". Centralizado para os 4 eventos terminais
  // (estrutura limpa / com apontamentos; cruzamentos limpos / com
  // apontamentos) ficarem consistentes.
  const concludedTimeLabel = (iso) =>
    iso
      ? t("modeling_inputs_v3.now_pane.timeline.concluded_at", {
          when: formatDeliveredAt(iso),
        })
      : t("modeling_inputs_v3.now_pane.timeline.concluded_time");

  // Titulo e sub do item "periodo aberto" dependem da natureza do input:
  //   - monthly: "aberto para recebimento" + "Aguardando envio" (enquanto
  //     nada chegou).
  //   - semi-fixed/fixed: "aberto para revisao" sem sub — o dado ja
  //     existe de periodos anteriores, o usuario esta aqui so para
  //     conferir/atualizar se precisar, nao para "entregar".
  const openTitle = (
    <Trans
      i18nKey={
        isPermanentNature
          ? "modeling_inputs_v3.now_pane.timeline.open_review"
          : "modeling_inputs_v3.now_pane.timeline.open_receive"
      }
      components={{ 1: <strong /> }}
    />
  );
  let openSub = null;
  if (!isPermanentNature && !hasDelivered) {
    openSub = t("modeling_inputs_v3.now_pane.timeline.open_awaiting");
  }
  items.push({
    id: "open",
    kind: "info",
    icon: CompareArrows,
    time: t("modeling_inputs_v3.now_pane.timeline.open_time"),
    title: openTitle,
    sub: openSub,
  });

  // Input opcional concluido sem arquivo (waived). O usuario marcou
  // explicitamente que este input nao precisa de envio neste mes
  // (ou em qualquer mes do FY corrente, no caso de yearly). Tratamos
  // como um "fato" cronologico assim como "Arquivo recebido" — quem
  // marcou e quando — pra que a timeline nao apareca vazia e o
  // usuario tenha trilha de auditoria. Encerra a narrativa: sem
  // arquivo, nao ha "Arquivo recebido" / "Estrutura lida" / etc.
  if (isWaived) {
    const waivedLabel = waivedAt
      ? formatDeliveredAt(waivedAt)
      : t("modeling_inputs_v3.now_pane.timeline.recently");
    const waiveTitle = waivedByName ? (
      <Trans
        i18nKey="modeling_inputs_v3.now_pane.timeline.waived_with_user"
        values={{ name: waivedByName }}
        components={{ 1: <strong />, 3: <strong /> }}
      />
    ) : (
      <Trans
        i18nKey="modeling_inputs_v3.now_pane.timeline.waived_no_user"
        components={{ 1: <strong /> }}
      />
    );
    items.push({
      id: "waived",
      kind: "ok",
      icon: CheckCircle,
      time: waivedLabel,
      title: waiveTitle,
      sub: t("modeling_inputs_v3.now_pane.timeline.waived_sub"),
    });
    return items;
  }

  if (!hasDelivered && !isUploading) {
    return items;
  }

  if (busyAction === "reprocess") {
    items.push({
      id: "reprocessing-existing-file",
      kind: "pending",
      time: t("modeling_inputs_v3.now_pane.timeline.now_time"),
      title: (
        <strong>
          {t("modeling_inputs_v3.now_pane.timeline.reprocessing_title")}
        </strong>
      ),
      sub: t("modeling_inputs_v3.now_pane.timeline.reprocessing_sub"),
    });
    return items;
  }

  // Fase 1 — upload HTTP em curso (lado do cliente). O arquivo ainda
  // NAO chegou ao backend: o unico item deve ser "Enviando arquivo"
  // como pending (sem "Arquivo recebido" nem "Lendo estrutura", que
  // sao fases posteriores).
  //
  // ``uploadProgress`` vem do Redux (``file_transfer.progress``) e
  // permite exibir uma barra real de 0-100% enquanto o axios dispara
  // ``onUploadProgress``. Quando o envio termina (100%) mas o backend
  // ainda nao respondeu, mantemos o item como "pending" — a barra
  // fica cheia, o texto continua indicando transferencia em curso,
  // ate o servidor confirmar e popular ``control``.
  if (isUploading) {
    const pct = Math.max(0, Math.min(100, Number(uploadProgress) || 0));
    const subText =
      pct >= 100
        ? t("modeling_inputs_v3.now_pane.timeline.uploading_done")
        : t("modeling_inputs_v3.now_pane.timeline.uploading_progress", { pct });
    items.push({
      id: "uploading",
      kind: "pending",
      time: t("modeling_inputs_v3.now_pane.timeline.now_time"),
      title: (
        <strong>
          {t("modeling_inputs_v3.now_pane.timeline.uploading_title")}
        </strong>
      ),
      sub: subText,
      progress: pct,
    });
    return items;
  }

  // Fase 2 — arquivo ja foi recebido pelo backend (control existe).
  const deliveredLabel = control?.delivered_at
    ? formatDeliveredAt(control.delivered_at)
    : t("modeling_inputs_v3.now_pane.timeline.recently");
  // Autor do upload — vem de ``state_params.uploaded_by`` (ou seja,
  // ``InputControlV3.updated_by``), NAO do responsavel catalogado.
  // Se o backend nao registrou quem subiu, fica generico sem atribuir
  // a ninguem em particular. Nunca caimos no primeiro responsavel
  // aqui — isso mente sobre quem entregou.
  const uploadTitle = uploaderName ? (
    <Trans
      i18nKey="modeling_inputs_v3.now_pane.timeline.upload_with_user"
      values={{ name: uploaderName }}
      components={{ 1: <strong /> }}
    />
  ) : (
    <strong>{t("modeling_inputs_v3.now_pane.timeline.upload_no_user")}</strong>
  );
  items.push({
    id: "upload",
    kind: "info",
    icon: CloudUpload,
    time: deliveredLabel,
    title: uploadTitle,
    sub: control?.original_filename
      ? t("modeling_inputs_v3.now_pane.timeline.upload_sub_with_file", {
          filename: control.original_filename,
        })
      : t("modeling_inputs_v3.now_pane.timeline.upload_sub_no_file"),
  });

  // Fase 3a — enfileirado: backend recebeu mas o worker V3 ainda nao
  // pegou o job.
  if (control?.state === "uploaded") {
    items.push({
      id: "queued-pending",
      kind: "pending",
      time: t("modeling_inputs_v3.now_pane.timeline.in_progress_time"),
      title: (
        <strong>
          {t("modeling_inputs_v3.now_pane.timeline.queued_title")}
        </strong>
      ),
      sub: t("modeling_inputs_v3.now_pane.timeline.queued_sub"),
    });
    return items;
  }

  // Fase 3b — worker rodando: parse/structure check em curso.
  if (control?.state === "processing") {
    items.push({
      id: "parsing-pending",
      kind: "pending",
      time: t("modeling_inputs_v3.now_pane.timeline.in_progress_time"),
      title: (
        <strong>
          {t("modeling_inputs_v3.now_pane.timeline.parsing_title")}
        </strong>
      ),
      sub: t("modeling_inputs_v3.now_pane.timeline.parsing_sub"),
    });
    return items;
  }

  if (notesLoading) {
    items.push({
      id: "notes-loading",
      kind: "pending",
      time: t("modeling_inputs_v3.now_pane.timeline.in_progress_time"),
      title: (
        <strong>
          {t("modeling_inputs_v3.now_pane.timeline.notes_loading_title")}
        </strong>
      ),
      sub: t("modeling_inputs_v3.now_pane.timeline.notes_loading_sub"),
    });
    return items;
  }

  if (notesError) {
    items.push({
      id: "notes-error",
      kind: "err",
      icon: Error,
      time: t("modeling_inputs_v3.now_pane.timeline.error_time"),
      title: (
        <strong>
          {t("modeling_inputs_v3.now_pane.timeline.notes_error_title")}
        </strong>
      ),
      sub: t("modeling_inputs_v3.now_pane.timeline.notes_error_sub"),
    });
    return items;
  }

  if (control?.state === "system_error") {
    // Mensagem escrita em linguagem de negocio — sem jargao de infra,
    // sem mencao a "job", "execucao", "system_error". O usuario final
    // (consultor/cliente) so precisa saber duas coisas: (1) deu
    // problema de sistema e (2) o caminho e acionar o time da
    // Integration. Qualquer detalhe tecnico fica para logs internos.
    items.push({
      id: "system-error",
      kind: "err",
      icon: Error,
      time: t("modeling_inputs_v3.now_pane.timeline.error_time"),
      title: (
        <strong>
          {t("modeling_inputs_v3.now_pane.timeline.system_error_title")}
        </strong>
      ),
      sub: t("modeling_inputs_v3.now_pane.timeline.system_error_sub"),
    });
    return items;
  }

  // Narrativa estrutural.
  //
  // Regras (user feedback 2026-04-17):
  //   1. LEITURA DE CIMA PARA BAIXO E CRONOLOGICA. O backend ja entrega
  //      as notas ordenadas por ``created_at`` ASC — a UI nao reordena.
  //   2. Observacoes (sev 1) e pontos de atencao (sev 2) aparecem como
  //      ITENS INDIVIDUAIS, em ordem cronologica. ``time`` traz o label
  //      de severidade ("Ponto de atencao"); ``title`` exibe o corpo
  //      real da nota (``notes``); ``sub`` fica vazio — nao repetimos
  //      o template "Verifica se ...".
  //   3. Erros (sev 3) sao CONSOLIDADOS em UM UNICO item final
  //      ("Arquivo rejeitado"): ``time`` = "Arquivo rejeitado",
  //      ``title`` = "Arquivo rejeitado na leitura" (negrito fixo),
  //      ``sub`` = corpo(s) do(s) erro(s) concatenados. Isso encaixa
  //      a narrativa: "o pipeline olhou N coisas, algumas merecem
  //      atencao, e finalmente o arquivo foi rejeitado por: <motivo>".
  //   4. Sem erros + sem avisos -> um unico item "sem apontamentos".
  const structureNotes = notes.filter((n) => n.check_type === "STR");
  const lowNotes = structureNotes.filter((n) => n.severity !== 3);
  const errorNotes = structureNotes.filter((n) => n.severity === 3);

  // Datetime da conclusao da leitura da estrutura. Fonte primaria:
  // ``control.parsed_at`` (job STRUCTURE_CHECK concluido — exposto pelo
  // backend, imune a bumps de ``updated_at``). Fallback resiliente para
  // backend antigo sem ``parsed_at``: ``MAX(created_at)`` das notas STR
  // (so existe quando ha apontamentos). ``null`` => rotulo generico.
  const structDoneAt =
    control?.parsed_at ||
    maxIsoDate(structureNotes.map((n) => n.created_at)) ||
    null;

  if (structureNotes.length === 0) {
    items.push({
      id: "structure-clean",
      kind: "ok",
      icon: CheckCircle,
      time: concludedTimeLabel(structDoneAt),
      title: (
        <strong>
          {t("modeling_inputs_v3.now_pane.timeline.struct_clean_title")}
        </strong>
      ),
      sub: t("modeling_inputs_v3.now_pane.timeline.struct_clean_sub"),
    });
  } else {
    // (a) Observacoes/avisos consolidados em UM unico item da timeline,
    // com cada nota como sub-card demarcado (mesmo padrao usado pelos
    // cruzamentos / CSL — ver bloco abaixo). Antes emitiamos UMA linha
    // por nota; com inputs ricos em estrutura (ex.: planilhas com 5+
    // sev=1 e sev=2 simultaneos) a timeline ficava saturada e a
    // "caixa" demarcada de cada apontamento sumia. Visual de cada
    // sub-card herda ``tlCrossCard*`` -- nada novo de CSS, so o
    // gatilho de severidade muda. ``kind`` do item-mae reflete a
    // pior severidade observada (warn > note).
    if (lowNotes.length > 0) {
      const noteSubItems = lowNotes.map((note, idx) => {
        const sk = SEVERITY_KIND[note.severity] || SEVERITY_KIND[1];
        const skLabel = t(sk.labelKey);
        const body = renderI18n(note.notes, lang);
        const templateTitle = renderI18n(note.check_description, lang);
        const headline =
          body ||
          templateTitle ||
          formatStructureNoteBody(note, lang, t) ||
          skLabel;
        const sevKind = sevKindFor(note.severity);
        return {
          id: `note-sub:${note.id || idx}`,
          sevKind,
          severityLabel: skLabel,
          title: templateTitle || skLabel,
          body: headline,
          // "Marcar como analisado": so warnings (sev=2) sao
          // analisaveis. ``noteId`` alimenta o endpoint de toggle; os
          // campos ``marked*`` renderizam o carimbo de auditoria.
          noteId: note.id || null,
          severity: note.severity,
          markedVerified: !!note.marked_as_verified,
          markedBy: note.marked_as_verified_by || null,
          markedAt: note.marked_as_verified_at || null,
        };
      });
      const maxLowSev = lowNotes.reduce(
        (acc, n) => Math.max(acc, n.severity || 0),
        0,
      );
      const motherKind = maxLowSev === 2 ? "warn" : "note";
      const MotherIcon = SEVERITY_KIND[maxLowSev || 1]?.Icon || InfoOutlined;
      const motherTitle = t(
        "modeling_inputs_v3.now_pane.timeline.struct_notes_count",
        { count: lowNotes.length },
      );
      // ``motherTime`` rotula o item-mae da timeline. Antes oscilava
      // entre "Pontos de atencao" (sev=2) e "Observacoes" (sev=1) — o
      // que confundia o consultor: "Pontos de atencao" era irmao de
      // "Cruzamentos" na timeline e parecia uma categoria estrutural
      // de mesmo nivel. Agora a categoria e fixa: "Leitura do
      // arquivo" (refere-se ao parsing/checagem da estrutura do
      // input). O que diferencia sev=1 vs sev=2 e a cor dos
      // sub-cards e o cabecalho de grupo (Informativos / Pontos de
      // atencao) renderizado abaixo.
      const motherTime = t(
        "modeling_inputs_v3.now_pane.timeline.struct_reading_time",
      );
      items.push({
        id: "structure-notes-summary",
        kind: motherKind,
        icon: MotherIcon,
        time: motherTime,
        title: <strong>{motherTitle}</strong>,
        sub: null,
        noteSubItems,
      });
    }

    // (a.2) "Concluido com apontamentos" — so quando NAO ha erros.
    // Fecha narrativamente o bloco de estrutura: o usuario ve os
    // apontamentos individuais acima e, no final, a confirmacao de
    // que a leitura encerrou com aceite. Sem este item, um arquivo
    // aceito-com-apontamentos (ex.: Tarifario ID) ficava sem o passo
    // "Concluido" explicito que o Acarreo IFAC tem (estrutura limpa).
    // Quando ha erros, "Arquivo rejeitado" ja cumpre esse papel
    // narrativo — nao sobrepor duas conclusoes.
    if (lowNotes.length > 0 && errorNotes.length === 0) {
      const count = lowNotes.length;
      items.push({
        id: "structure-concluded-with-notes",
        kind: "ok",
        icon: CheckCircle,
        time: concludedTimeLabel(structDoneAt),
        title: (
          <strong>
            {t(
              "modeling_inputs_v3.now_pane.timeline.struct_concluded_with_notes",
              { count },
            )}
          </strong>
        ),
        sub: t(
          "modeling_inputs_v3.now_pane.timeline.struct_concluded_with_notes_sub",
        ),
      });
    }

    // (b) Erros consolidados em UM item "Arquivo rejeitado".
    if (errorNotes.length > 0) {
      const errorBodies = errorNotes
        .map((n) => formatStructureNoteBody(n, lang, t))
        .filter(Boolean);
      // Juntamos os corpos com quebra de linha para manter cada motivo
      // legivel. Quando ha apenas 1 erro (caso tipico), vira linha
      // unica — sem artefato de formatacao.
      const subText = errorBodies.join("\n");
      items.push({
        id: "structure-rejected",
        kind: "err",
        icon: Error,
        time: t("modeling_inputs_v3.now_pane.timeline.rejected_time"),
        title: (
          <strong>
            {t("modeling_inputs_v3.now_pane.timeline.rejected_title")}
          </strong>
        ),
        sub:
          subText ||
          t("modeling_inputs_v3.now_pane.timeline.rejected_sub_fallback"),
      });
    }
  }

  // (c) Cruzamentos (CSL). Estrutura analoga a Estrutura (bloco acima):
  //   - 1 item-mae "Cruzamentos" com sub-cards agrupados por
  //     severidade (note / warn / err) — mesma renderizacao usada
  //     pelas notas STR.
  //   - 1 item "Concluido" so quando NAO ha erros — fecha
  //     narrativamente o passo (igual ao "Estrutura lida com N
  //     apontamentos" que ja existe).
  //   - Quando ha erros (sev=3), 1 item adicional "Cruzamentos
  //     bloquearam o avanco" — equivalente a "Arquivo rejeitado"
  //     da estrutura. Ambos param o pipeline.
  //   - Quando ``xlState === 'success'`` e nao ha notas, emitimos um
  //     unico item "Cruzamentos verificados sem apontamentos" — o
  //     espelho positivo de "Estrutura lida sem apontamentos".
  //   - Quando ``xlState === null`` ou nao ha cross-links declarados,
  //     omitimos o bloco — input que nao participa de cruzamento
  //     nao precisa de linha placeholder.
  const crossLinkNotes = notes.filter((n) => n.check_type === "CSL");
  // Onda 3.4 (CCU Testes, mai/2026): cross-links com decisao
  // ``accept`` ja respondida sao filtrados de ``xlErr``. O CSL
  // permanece como notas de severidade 3 (o run original NAO e'
  // re-executado pela decisao), entao sem este filtro a timeline
  // continuava mostrando "Avanco suspenso por cruzamento" mesmo
  // apos o consultor ter explicitamente aceitado o desvio. Listamos
  // o evento "Decisao respondida" mais abaixo (sortido em
  // ``answeredDecisions``) — isso ja basta como rastro.
  // Onda 2026-05-17: a deteccao de "decisao aceita" agora consulta
  // 3 fontes em ordem decrescente de confiabilidade:
  //   1) ``resolved_payload.decision === 'accept'`` (fonte canonica
  //      — copia exata da opcao escolhida pelo usuario);
  //   2) ``chosen_action === 'accept'`` (deduzido pelo backend via
  //      humanizer; pode ficar nulo se a ordem das opcoes mudou);
  //   3) ``chosen_label`` contendo "aceit" / "accept" (heuristica
  //      final, util quando o backend antigo nao expoe os campos
  //      acima — evita timeline narrar "Avanco suspenso" para
  //      cruzamento ja aceito).
  const _isAcceptDecision = (d) => {
    const rp = d?.resolved_payload;
    if (rp && typeof rp === "object") {
      const kind = rp.decision || rp.action;
      if (kind === "accept") return true;
      if (kind && kind !== "accept") return false;
    }
    if (d?.chosen_action === "accept") return true;
    if (d?.chosen_action && d.chosen_action !== "accept") return false;
    const label = (d?.chosen_label || "").toLowerCase();
    return /aceit|accept/.test(label);
  };
  const acceptedXlinkMetas = new Set();
  if (Array.isArray(answeredDecisions)) {
    answeredDecisions.forEach((d) => {
      if (d?.scope !== "cross_link") return;
      if (!_isAcceptDecision(d)) return;
      const m = d?.cross_link?.meta_name || d?.meta_name || null;
      if (m) acceptedXlinkMetas.add(m);
    });
  }
  // ``isAcceptedXlinkNote``: nota CSL de um xlink ja "absorvido" via
  // aceite. Duas fontes equivalentes — bastando UMA marcar aceito:
  //
  //   (a) ``answeredDecisions`` (lista de decisoes ja respondidas neste
  //       periodo) -> alimenta ``acceptedXlinkMetas`` acima;
  //   (b) ``crossLinksByMeta[meta].decision_accept`` (anexado pelo
  //       backend ao proprio xlink quando ja houve resposta ``accept``).
  //
  // Duplicidade intencional: a hidratacao via ``answered_decisions``
  // depende do endpoint ``/drawer-details/`` e do shape do
  // ``_serialize_decision`` (que precisa de ``chosen_action`` /
  // ``resolved_payload`` para deduzir o aceite). O ``decision_accept``
  // anexado por xlink e' computado direto no backend a partir do
  // ``CrossLinkControlV3.state`` e da decisao mais recente — mais
  // estavel quando ha defasagem de hidratacao entre os dois canais
  // (visto no POC CCU Testes: timeline mostrava "Avanco suspenso"
  // mesmo apos aceite porque a decisao respondida nao havia entrado
  // ainda na lista ``answeredDecisions``, embora o xlink ja
  // carregasse ``decision_accept``).
  const isAcceptedXlinkNote = (n) => {
    const m = n?.cross_link_meta_name;
    if (!m) return false;
    if (acceptedXlinkMetas.has(m)) return true;
    const xl = crossLinksByMeta?.get?.(m);
    return Boolean(xl?.decision_accept);
  };
  const xlErr = crossLinkNotes.filter(
    (n) => (n.severity || 0) === 3 && !isAcceptedXlinkNote(n),
  );

  // Lista de display_names limpos vinda do backend — usada como
  // contador de "X cruzamentos limpos". Onda 3.4 (CCU Testes,
  // mai/2026): o copy do item ``cross-clean`` foi simplificado para
  // nao listar mais os nomes (so a contagem), entao mantemos apenas
  // o ``length``.
  const cleanNamesList = Array.isArray(cleanCompletedNames)
    ? cleanCompletedNames.filter(Boolean)
    : [];

  const total = typeof xlCount === "number" && xlCount > 0 ? xlCount : null;
  // Estado de completude do conjunto de cross-links do input, vindo do
  // mesmo payload consolidado do drawer que alimenta a aba
  // "Cruzamentos". Isso resolve a ambiguidade: um xlink em warning
  // pode significar "todos terminaram, alguns com apontamento" OU
  // "alguns terminaram com apontamento, mas ainda falta rodar o resto".
  // So usamos o fechamento narrativo "Concluido com N apontamentos"
  // no primeiro caso.
  // Filtra xlinks fora do periodo (``out_of_period === true``): eles
  // aparecem na aba "Cruzamentos" com a tag de vigencia, mas NAO
  // contam como pendentes para o agregado da timeline. Sem isso, um
  // xlink que so vai entrar em vigor em fev/2026 (ex.: consolidado
  // ``merchans_vs_ventas``) fazia ``xlinksAllSettled = false`` em
  // abril/2024 e o NowPane mostrava eternamente "Conclusao dos
  // cruzamentos / Verificacoes em andamento" — mesmo apos todos os
  // xlinks vigentes terem rodado.
  const xlinkEntries = Array.from(crossLinksByMeta?.values?.() || []).filter(
    (xl) => !xl?.out_of_period,
  );
  const unfinishedXlinkStates = new Set([
    null,
    undefined,
    "pending",
    "blocked",
    "stale",
    "running",
    "processing",
  ]);
  const xlinksAllSettled =
    xlinkEntries.length > 0 &&
    xlinkEntries.every((xl) => !unfinishedXlinkStates.has(xl?.control?.state));

  // Datetime da conclusao dos cruzamentos = ``last_run_at`` do ULTIMO
  // cruzamento a rodar (MAX entre os xlinks vigentes). So e' valido
  // quando TODOS os cruzamentos ja liquidaram (``xlinksAllSettled``);
  // em cenario parcial fica ``null`` e os eventos caem no rotulo
  // generico ("Cruzamentos" / "Concluido" sem data).
  const crossDoneAt = xlinksAllSettled
    ? maxIsoDate(xlinkEntries.map((xl) => xl?.control?.last_run_at))
    : null;

  // (1) Cross-links concluidos sem apontamento: sempre aparecem quando
  // ha ao menos UM cruzamento limpo, mesmo que outros do mesmo input
  // estejam com apontamentos. Onda 3.4 (CCU Testes, mai/2026): copy
  // simplificado para "X de N cruzamentos sem apontamentos" + sub fixo
  // "Comparação dos dados conferiu com o esperado". A lista de quais
  // cruzamentos foram verificados ficava ruidosa quando havia 20+
  // xlinks no input — usuario decidiu que a info nao agrega valor
  // operacional (quem precisar ve na aba "Cruzamentos").
  //
  // ``effectiveDone``: quando o backend confirma estado ``success``
  // global do conjunto de cross-links sem mandar a lista nominal
  // (cenario de implementacoes mais antigas, anteriores ao
  // ``clean_completed_names``), assumimos que todos os xlinks
  // declarados (``total``) foram verificados limpos.
  const effectiveDone =
    xlState === "success" && crossLinkNotes.length === 0
      ? total || cleanNamesList.length
      : cleanNamesList.length;
  if (effectiveDone > 0) {
    const totalForCopy = total != null ? total : effectiveDone;
    items.push({
      id: "cross-clean",
      kind: "ok",
      icon: CheckCircle,
      // Quando todos os cruzamentos liquidaram, este e' o evento de
      // conclusao (limpo) — vira "Concluido em <ultimo cruzamento>".
      // Em cenario parcial (``crossDoneAt`` null) mantem o rotulo
      // "Cruzamentos", pois ainda ha cruzamentos por rodar.
      time: crossDoneAt
        ? concludedTimeLabel(crossDoneAt)
        : t("modeling_inputs_v3.now_pane.timeline.cross_time"),
      title: (
        <strong>
          {t("modeling_inputs_v3.now_pane.timeline.cross_clean_title", {
            done: effectiveDone,
            total: totalForCopy,
          })}
        </strong>
      ),
      sub: t("modeling_inputs_v3.now_pane.timeline.cross_clean_sub"),
    });
  }

  if (crossLinkNotes.length > 0) {
    const buildCrossSubItems = (notesSubset, groupKey) =>
      notesSubset.map((note, idx) => {
        // Titulo do sub-card: ``display_name`` do descritor (snapshot)
        // — mesma fonte que a aba "Cruzamentos" usa. A nota traz o
        // ``cross_link_meta_name`` (chave estavel); resolvemos via
        // ``crossLinksByMeta`` montado no componente pai a partir de
        // ``drawerDetails.crossLinks``. Fallback discreto para
        // "Cruzamento" cobre notas legadas sem ``cross_link_meta_name``
        // (ex.: bancos populados antes da Leva 73-bis).
        const xlMeta = note.cross_link_meta_name || null;
        const xlEntry = xlMeta ? crossLinksByMeta.get(xlMeta) : null;
        const counterpartLabel =
          (xlEntry && xlEntry.display_name) ||
          (xlEntry && xlEntry.meta_name) ||
          t("modeling_inputs_v3.now_pane.timeline.cross_fallback");
        // Convencao do framework: o apontamento dinamico vive em
        // ``notes`` (ex.: check_missing_values). Resolvers custom (ex.:
        // node-bridge coverage do CCU) gravam a frase descritiva em
        // ``check_description`` e deixam ``notes`` nulo — o fallback
        // garante que o statement do cruzamento apareca tambem nesses
        // casos, em vez de ficar so com o titulo + badge de %.
        const body =
          renderI18n(note.notes, lang) ||
          renderI18n(note.check_description, lang);
        // ``associated_values`` traz tanto a amostra de chaves nao
        // cruzadas (``missing_sample``, ``missing_count``, blob path)
        // quanto as fracoes que viram o "indice de qualidade" do
        // sub-card (``missing_impact_ratio`` / ``missing_ratio``).
        // Lemos UMA vez aqui e reusamos abaixo.
        const av = note.associated_values || {};
        // ``qualityRatio``: fracao 0..1 dos valores que efetivamente
        // cruzaram (= 1 - missing_ratio). Preferimos ``missing_impact_ratio``
        // (ponderado pela medida do fact, ex.: receita) em vez de
        // ``missing_ratio`` (linhas), porque o consultor pensa em
        // "% do volume que casou", nao "% das chaves". Quando nada
        // disso esta disponivel (notas legadas), ``qualityRatio = null``
        // e a UI omite o badge.
        const missingFrac = (() => {
          const cand =
            av.missing_impact_ratio !== undefined
              ? av.missing_impact_ratio
              : av.missing_ratio;
          if (cand === undefined || cand === null) return null;
          const n = Number(cand);
          if (!Number.isFinite(n)) return null;
          return Math.min(1, Math.max(0, n));
        })();
        const qualityRatio = missingFrac === null ? null : 1 - missingFrac;
        const missingSample = Array.isArray(av.missing_sample)
          ? av.missing_sample
          : [];
        // Padronizacao de severidade nas caixas (rodada 2026-04-27):
        //   sev=1 -> "note" (azul / informativo)
        //   sev=2 -> "warn" (amarelo / ponto de atencao)
        //   sev>=3 -> "err" (vermelho / bloqueante)
        // Antes sev=1 cai em "ok" (verde), o que mentia: caixa verde
        // sugere "passou bem", mas sev=1 e' uma observacao real do
        // executor. Verde fica reservado a "concluido sem
        // apontamentos", nao a apontamento informativo.
        const sevKind = sevKindFor(note.severity);
        // Limites (atencao/aceitacao) NAO sao mais exibidos inline na
        // timeline — a redacao ficava ruidosa e o consultor argumentou
        // que o lugar correto pra eles e a aba "Configura\u00e7\u00e3o" do input
        // (TODO aberto na documentacao da revamp). Mantem-se a leitura
        // do ``threshold`` no payload caso outro componente futuro queira
        // exibir, mas o array ``meta`` do sub-card fica vazio por design.
        const meta = [];
        // Onda 3.4 (CCU Testes, mai/2026): quando o xlink ja teve
        // resposta de D-22 (``decision_reject`` presente no
        // descritor), anexamos no sub-card um complemento sutil
        // "Decisao: {who} respondeu {choice} em {when}" para fechar
        // o ciclo da pergunta na MESMA linha visual do ponto critico.
        // Antes, esse evento aparecia como item independente da
        // timeline ("decision-answered:*"), o que duplicava info.
        const decReject = xlEntry?.decision_reject || null;
        const decAccept = xlEntry?.decision_accept || null;
        const someoneFallback = t(
          "modeling_inputs_v3.now_pane.timeline.someone_fallback",
        );
        const decisionInfo = decReject
          ? {
              kind: "reject",
              answeredBy: decReject.answered_by || someoneFallback,
              answeredAt: decReject.answered_at || null,
              rejectedDisplayName:
                decReject.rejected_display_name ||
                decReject.rejected_meta ||
                "—",
            }
          : decAccept
            ? {
                kind: "accept",
                answeredBy: decAccept.answered_by || someoneFallback,
                answeredAt: decAccept.answered_at || null,
              }
            : null;
        return {
          id: `csl-sub:${groupKey}:${note.id || idx}`,
          sevKind,
          title: counterpartLabel,
          // ``qualityRatio``: fracao 0..1 dos valores que cruzaram —
          // exibida como badge "%XX" no canto superior direito do
          // sub-card. ``null`` quando a nota nao traz ratio (esconde
          // o badge).
          qualityRatio,
          body,
          meta,
          missingSample,
          missingTotal: av.missing_count,
          // ``noteId``: ID UUID da ``InputCheckNoteV3`` para o endpoint
          // ``GET /api/v3/cross-links/notes/<id>/missing.csv`` baixar a
          // lista completa (Leva 73-bis). Pode vir nulo em notas legadas
          // anteriores ao executor que materializa o blob.
          noteId: note.id || null,
          hasMissingBlob: Boolean(av.missing_keys_blob_path),
          decisionInfo,
          xlMeta,
          // "Marcar como analisado" (paridade com as notas STR): so
          // warnings (sev=2) recebem o botao; os campos ``marked*``
          // renderizam o carimbo "analisado por X em [data]".
          severity: note.severity,
          markedVerified: !!note.marked_as_verified,
          markedBy: note.marked_as_verified_by || null,
          markedAt: note.marked_as_verified_at || null,
        };
      });

    const pushCrossGroup = ({
      id,
      notesSubset,
      kind,
      icon,
      groupKey, // i18n key suffix: 'info' | 'attention' | 'critical'
    }) => {
      if (!Array.isArray(notesSubset) || notesSubset.length === 0) return;
      const n = notesSubset.length;
      const prefix =
        total != null
          ? t(
              "modeling_inputs_v3.now_pane.timeline.cross_count_prefix_n_of_total",
              {
                n,
                total,
              },
            )
          : String(n);
      const titleText = t(
        `modeling_inputs_v3.now_pane.timeline.cross_group_${groupKey}`,
        { prefix, count: n },
      );
      items.push({
        id,
        kind,
        icon,
        time: t("modeling_inputs_v3.now_pane.timeline.cross_time"),
        title: <strong>{titleText}</strong>,
        sub: null,
        crossSubItems: buildCrossSubItems(notesSubset, id),
      });
    };

    const xlInfo = crossLinkNotes.filter((n) => {
      const sev = Number(n.severity || 0);
      return sev > 0 && sev < 2;
    });
    const xlAttention = crossLinkNotes.filter(
      (n) => Number(n.severity || 0) === 2,
    );
    const xlCritical = crossLinkNotes.filter(
      (n) => Number(n.severity || 0) >= 3,
    );

    // (2) Apontamentos nao criticos / informativos.
    pushCrossGroup({
      id: "cross-notes",
      notesSubset: xlInfo,
      kind: "note",
      icon: SEVERITY_KIND[1]?.Icon || CompareArrows,
      groupKey: "info",
    });

    // (3) Apontamentos em nivel de atencao.
    pushCrossGroup({
      id: "cross-attention",
      notesSubset: xlAttention,
      kind: "warn",
      icon: SEVERITY_KIND[2]?.Icon || CompareArrows,
      groupKey: "attention",
    });

    // (4) Criticos / perguntas bloqueantes, quando existirem.
    pushCrossGroup({
      id: "cross-critical",
      notesSubset: xlCritical,
      kind: "err",
      icon: SEVERITY_KIND[3]?.Icon || Error,
      groupKey: "critical",
    });

    // Fechamento narrativo — so aparece quando TODOS os xlinks do input
    // ja estao liquidados. Em cenario parcial, os grupos acima ja
    // comunicam o que rodou, e a proxima fase segue sendo "Conclusao
    // dos cruzamentos".
    const nonCriticalNotes = crossLinkNotes.filter(
      (n) => Number(n.severity || 0) < 3,
    );
    if (xlinksAllSettled && xlErr.length === 0 && nonCriticalNotes.length > 0) {
      const count = nonCriticalNotes.length;
      items.push({
        id: "cross-concluded-with-notes",
        kind: "ok",
        icon: CheckCircle,
        time: concludedTimeLabel(crossDoneAt),
        title: (
          <strong>
            {t(
              "modeling_inputs_v3.now_pane.timeline.cross_concluded_with_notes",
              { count },
            )}
          </strong>
        ),
        sub: t(
          "modeling_inputs_v3.now_pane.timeline.cross_concluded_with_notes_sub",
        ),
      });
    } else if (xlErr.length > 0) {
      // Onda 3.4 (CCU Testes, mai/2026): removemos o ``sub`` com o
      // texto longo das notas (``errBodies``) — o mesmo detalhe ja
      // aparece imediatamente acima no sub-card do cruzamento que
      // falhou (ver ``buildCrossSubItems`` em ``cross-critical``).
      // Repetir aqui inflava a timeline e confundia o usuario que
      // achava serem dois apontamentos diferentes.
      items.push({
        id: "cross-blocked",
        kind: "err",
        icon: Error,
        time: t("modeling_inputs_v3.now_pane.timeline.cross_blocked_time"),
        title: (
          <strong>
            {t("modeling_inputs_v3.now_pane.timeline.cross_blocked_title")}
          </strong>
        ),
      });
    }
  }

  // Onda 1.9 (mai/2026): fallback "cruzamento critico sem nota" — o
  // backend agrega ``cross_linking.state==='error'`` no snapshot
  // (consumido aqui via ``xlState``) ANTES de propagar as notas CSL
  // pelo endpoint ``/drawer-details/`` (que usa filtro mais estrito
  // por job/ano/mes e pode chegar 1 tick depois). Sem este branch, o
  // header do drawer ja mostrava "Cruzamento critico" e a caixa
  // "Cruzamento" mostrava "Pontos criticos", mas a timeline ficava
  // muda — o usuario nao tinha onde clicar para entender o que
  // bloqueou.
  //
  // Onda 2026-05-17: suprimimos esse fallback quando todo bloqueio
  // critico ja foi absorvido por aceite (via ``answeredDecisions``
  // ou via ``crossLinksByMeta[meta].decision_accept``). Considerar
  // tambem o agregado de xlinks (e nao so' as notas CSL) porque:
  //
  //   - pode haver xlinks em ``state="error"`` sem nota CSL
  //     correspondente carregada ainda (delay entre snapshot e
  //     drawer-details);
  //   - mesmo apos o aceite, o backend muda o xlink para ``warning``
  //     no proximo tick — mas durante a janela curta entre o aceite
  //     e o re-poll, o ``xlState`` agregado pode ainda dizer
  //     ``error``. Sem essa salvaguarda, a timeline "pisca" o
  //     "Avanco suspenso" depois que o usuario clicou aceitar.
  const criticalNotes = crossLinkNotes.filter(
    (n) => Number(n.severity || 0) >= 3,
  );
  const allCriticalBlocksAccepted = (() => {
    // (a) Toda nota critica restante deve estar marcada como aceita.
    if (criticalNotes.length > 0) {
      if (!criticalNotes.every((n) => isAcceptedXlinkNote(n))) return false;
    }
    // (b) Pelo menos UMA evidencia de aceite tem que existir — se
    // nao houver nenhuma, "todo bloqueio aceito" eh vacuoso e nao
    // deve suprimir o evento ainda nao explicado.
    if (acceptedXlinkMetas.size > 0) return true;
    return xlinkEntries.some((xl) => xl?.decision_accept);
  })();
  if (
    (xlState === "error" || xlState === "system_error") &&
    criticalNotes.length === 0 &&
    !allCriticalBlocksAccepted
  ) {
    items.push({
      id: "cross-blocked-fallback",
      kind: "err",
      icon: Error,
      time: t("modeling_inputs_v3.now_pane.timeline.cross_blocked_time"),
      title: (
        <strong>
          {t(
            "modeling_inputs_v3.now_pane.timeline.cross_blocked_critical_title",
          )}
        </strong>
      ),
      sub: t("modeling_inputs_v3.now_pane.timeline.cross_blocked_critical_sub"),
    });
  }

  // Onda 3.4 (CCU Testes, mai/2026): coletamos as ``decision_id``
  // ja anexadas como sub-linha de sub-cards de cross-link critico
  // (ver ``buildCrossSubItems`` acima) para nao duplica-las como
  // item independente da timeline mais abaixo. Match feito por
  // ``decision_reject.decision_id`` que so existe quando o backend
  // anexou (cross-link com resposta ``reject_input``).
  const decisionIdsAlreadyShown = new Set();
  items.forEach((ev) => {
    if (!Array.isArray(ev.crossSubItems)) return;
    ev.crossSubItems.forEach((sub) => {
      if (!sub.xlMeta) return;
      const xl = crossLinksByMeta.get(sub.xlMeta);
      const did = xl?.decision_reject?.decision_id;
      if (did) decisionIdsAlreadyShown.add(String(did));
    });
  });

  // (d) Decisoes ja respondidas — fecha o ciclo da pergunta bloqueante
  // (P-13/D-22). Cada item registra "respondedor + escolha + quando".
  // Ordenamos por ``answered_at`` ASC para manter a narrativa
  // cronologica do drawer (notas vieram em ordem; decisoes seguem o
  // mesmo principio).
  if (Array.isArray(answeredDecisions) && answeredDecisions.length > 0) {
    const sorted = [...answeredDecisions]
      .filter((d) => !decisionIdsAlreadyShown.has(String(d?.id)))
      .sort((a, b) => {
        const ta = a?.answered_at ? new Date(a.answered_at).getTime() : 0;
        const tb = b?.answered_at ? new Date(b.answered_at).getTime() : 0;
        return ta - tb;
      });
    sorted.forEach((d) => {
      const who =
        d.answered_by_full_name ||
        d.answered_by_username ||
        t("modeling_inputs_v3.now_pane.timeline.someone_fallback");
      const choice =
        d.chosen_label ||
        t("modeling_inputs_v3.now_pane.timeline.decision_default_choice");
      // Contexto: cross-link tem display_name; demais scopes usam o
      // texto curto da pergunta (truncado) para nao fazer o card
      // virar romance.
      let contextLine;
      if (d.scope === "cross_link" && d.cross_link?.display_name) {
        contextLine = d.cross_link.display_name;
      } else if (d.question_md) {
        const flat = String(d.question_md).replace(/\s+/g, " ").trim();
        contextLine = flat.length > 140 ? `${flat.slice(0, 137)}…` : flat;
      } else {
        contextLine = null;
      }
      items.push({
        id: `decision-answered:${d.id}`,
        kind: "ok",
        icon: CheckCircle,
        time: d.answered_at
          ? formatDeliveredAt(d.answered_at)
          : t("modeling_inputs_v3.now_pane.timeline.decision_time"),
        title: (
          <span>
            <Trans
              i18nKey="modeling_inputs_v3.now_pane.timeline.decision_answered_title"
              values={{ who, choice }}
              components={{ 1: <strong />, 3: <strong /> }}
            />
          </span>
        ),
        sub: contextLine,
      });
    });
  }

  // Placeholder final da timeline — narra o que sobrou para o
  // proprio input (NUNCA placeholder do "calculo do mes", que e'
  // responsabilidade do drawer global de progresso).
  //
  // Onda 2026-05-17 (refinamento UI/UX): o evento de "proxima fase
  // / calculo do mes" foi substituido por um fechamento positivo
  // ("Todas as etapas concluidas para este input") sempre que nao
  // sobra nada a fazer DIRETAMENTE com o input — envio entregue,
  // estrutura OK, cruzamentos liquidados, sem perguntas em aberto.
  // Quando ainda ha cruzamentos rodando, mantemos o placeholder
  // "Conclusao dos cruzamentos". Estados bloqueantes
  // (``error``/``system_error``/``pending_decision``) nao emitem
  // placeholder — a barra de acao do drawer ja narra o que destrava.
  const xlBlocking =
    xlState === "error" ||
    xlState === "system_error" ||
    xlState === "pending_decision";
  if (!xlBlocking) {
    const xlPending =
      xlinkEntries.length > 0
        ? !xlinksAllSettled
        : xlState === "processing" ||
          xlState === "running" ||
          xlState === "partial" ||
          xlState === "pending" ||
          xlState === "blocked" ||
          xlState === "stale";
    if (xlPending) {
      items.push({
        id: "next-xlink",
        kind: "info",
        icon: Lock,
        time: t("modeling_inputs_v3.now_pane.timeline.next_phase_time"),
        title: (
          <span>
            {t("modeling_inputs_v3.now_pane.timeline.cross_concluding_title")}
          </span>
        ),
        sub: t("modeling_inputs_v3.now_pane.timeline.cross_concluding_sub"),
        muted: true,
      });
    } else {
      // Input fechou todas as suas etapas (envio + estrutura +
      // xlinks + perguntas). O calculo do mes em si segue sua propria
      // narrativa no drawer de progresso global — nao mistura aqui.
      items.push({
        id: "input-done",
        kind: "ok",
        // Onda 2026-05-17: ``Flag`` (chegada) substitui o ``CheckCircle``
        // genérico — comunica visualmente "linha de chegada" do
        // input. Material-UI v4 nao tem ``SportsScore`` (checkered
        // flag), entao usamos ``Flag`` simples como aproximacao
        // mais idiomatica.
        icon: Flag,
        // ``terminal`` sinaliza pro render que este eh o ULTIMO
        // evento e a linha conectora deve terminar dentro da
        // bolinha (ver classe ``tlItemTerminal`` no styles.js).
        terminal: true,
        time: t("modeling_inputs_v3.now_pane.timeline.concluded_time"),
        title: (
          <strong>
            {t("modeling_inputs_v3.now_pane.timeline.input_done_title")}
          </strong>
        ),
      });
    }
  }

  return items;
}

function formatDeliveredAt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm} · ${hh}:${min}`;
  } catch (_) {
    return String(iso);
  }
}

// Retorna a maior data (ISO string) de uma lista, ignorando valores
// vazios/invalidos. Usado para derivar o "datetime da conclusao" dos
// eventos da timeline (ex.: ultimo cruzamento concluido = MAX dos
// ``last_run_at``). Retorna ``null`` quando nao ha candidata valida.
function maxIsoDate(isoList) {
  let best = null;
  let bestMs = -Infinity;
  for (const iso of isoList || []) {
    if (!iso) continue;
    const ms = new Date(iso).getTime();
    if (Number.isFinite(ms) && ms > bestMs) {
      bestMs = ms;
      best = iso;
    }
  }
  return best;
}

// --- NowPane ---------------------------------------------------------------

const NowPane = ({
  input,
  isUploading,
  busyAction,
  isDownloading,
  downloadOptions,
  onDownload,
  notesState,
  decisionsState,
  // ``drawerDetails``: payload consolidado entregue pelo
  // ``useDrawerDetailsV3``. Aqui o NowPane usa apenas
  // ``crossLinks`` (mesma fonte que a aba "Cruzamentos") para
  // resolver o ``display_name`` legivel de cada nota CSL via
  // ``note.cross_link_meta_name``. Outros campos do drawerDetails
  // ja chegam aos panes via ``notesState`` / ``decisionsState``.
  drawerDetails,
  // ``isWaivePending``: true enquanto o toggle "Marcar como
  // concluido" esta no roundtrip da API. Aplica fade + bloqueia
  // interacao em stages + timeline para evitar que o usuario veja
  // dados antigos (ex.: arquivo entregue por Jairo) depois de ter
  // desligado o switch.
  isWaivePending,
  // Onda 3.4 (CCU Testes, mai/2026): propaga o estado de submissao
  // da D-22 (DecisionsCard -> NowPane -> InputDrawerV3) para que o
  // drawer bloqueie o fechamento (ESC, click no backdrop) enquanto
  // a chamada da API esta em curso. Evita o usuario fechar o drawer
  // por engano antes da decisao ser persistida.
  onDecisionSubmittingChange,
  // "Marcar como analisado" de um warning (sev=2): ``(noteId, verified)``
  // -> Promise. Origem: ``InputsV3.handleMarkNoteVerifiedV3`` (pause/
  // POST/resume). Quando ausente (storybook/teste), o botao some.
  onMarkNoteVerified,
}) => {
  const { clientColor } = useThemeColors();
  const classes = useDrawerStyles({ clientColor });
  const overlayScrollClasses = useOverlayScrollStyles();
  const wheelRef = useNativeWheelScroll();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "pt";

  // A-014: o botao "Baixar" foi movido do toolbar do drawer para
  // dentro da timeline, embutido logo apos o evento "Arquivo
  // recebido" (id "upload"). Aqui ancoramos o ``DownloadMenu`` no
  // botao concreto e centralizamos o open/close localmente — o pane
  // segue stateless do ponto de vista do drawer (ele recebe a lista
  // ``downloadOptions`` e o callback ``onDownload``).
  const downloadBtnRef = React.useRef(null);
  const [downloadAnchor, setDownloadAnchor] = React.useState(null);
  const safeDownloadOptions = downloadOptions || [];
  const canDownload = !isDownloading && safeDownloadOptions.length > 0;
  const handleToggleDownload = useCallback(() => {
    if (!canDownload) return;
    setDownloadAnchor((prev) => (prev ? null : downloadBtnRef.current));
  }, [canDownload]);
  const handleCloseDownload = useCallback(() => setDownloadAnchor(null), []);
  const handlePickDownload = useCallback(
    (kind) => {
      setDownloadAnchor(null);
      if (onDownload) onDownload(kind);
    },
    [onDownload],
  );

  // Progresso do upload HTTP — alimentado por
  // ``axiosClient.fileUploadClient.onUploadProgress``. Consumimos via
  // Redux aqui em vez de propagar por prop porque o valor muda dezenas
  // de vezes por upload e espalha facilmente.
  const uploadProgress = useSelector((s) => s?.file_transfer?.progress ?? 0);

  // Notas em voo no toggle "Marcar como analisado" (spinner/disable na
  // caixa). ``noteId -> true``.
  const [markingNotes, setMarkingNotes] = useState({});
  // Override OTIMISTA por nota (``noteId -> {markedVerified, markedBy,
  // markedAt}``). Aplicado ASSIM QUE o POST retorna (com nome+data
  // reais), SEM esperar o refetch consolidado — que pode demorar
  // (cadencia de poll, ETag, refresh de token). Sem isso a caixa
  // "voltava ao botao" apos o POST e so exibia o carimbo quando o
  // refetch chegava (UX ruim reportada). Como o ``noteId`` (UUID) e'
  // regenerado a cada reparse, o override nunca "vaza" para notas
  // recriadas; dentro do ciclo de vida da nota ele converge com o
  // servidor.
  const [optimisticMarks, setOptimisticMarks] = useState({});
  // Guard SINCRONO contra cliques rapidos no marcar/desmarcar. O
  // ``disabled={pending}`` ja cobre o caso comum, mas ``setMarkingNotes``
  // e' assincrono/batched: um duplo-clique no mesmo tick poderia disparar
  // dois POSTs (toggle conflitante) antes do re-render. Este Set (ref,
  // sem re-render) bloqueia a 2a chamada imediatamente.
  const markingInFlightRef = React.useRef(new Set());
  const handleMarkNote = useCallback(
    (noteId, verified) => {
      if (!noteId || typeof onMarkNoteVerified !== "function") return;
      if (markingInFlightRef.current.has(noteId)) return;
      markingInFlightRef.current.add(noteId);
      setMarkingNotes((m) => ({ ...m, [noteId]: true }));
      const clearPending = () => {
        markingInFlightRef.current.delete(noteId);
        setMarkingNotes((m) => {
          const next = { ...m };
          delete next[noteId];
          return next;
        });
      };
      Promise.resolve(onMarkNoteVerified(noteId, verified)).then(
        (serverNote) => {
          setOptimisticMarks((o) => ({
            ...o,
            [noteId]: {
              markedVerified: verified,
              markedBy:
                (serverNote && serverNote.marked_as_verified_by) || null,
              markedAt:
                (serverNote && serverNote.marked_as_verified_at) ||
                new Date().toISOString(),
            },
          }));
          clearPending();
        },
        () => {
          // Falha: nao aplica override (o toast de erro ja foi disparado
          // pelo parent); a caixa volta ao estado anterior.
          clearPending();
        },
      );
    },
    [onMarkNoteVerified],
  );

  // ``notesState`` vem pronto do ``InputDrawerV3`` via
  // ``useInputNotesV3`` — compartilhado entre as abas do drawer. Se,
  // por qualquer motivo, o pane for usado sem o drawer (storybook,
  // testes), aceitamos um fallback vazio para nao quebrar o render.
  const safeNotesState = notesState || {
    loading: false,
    notes: [],
    control: null,
    answeredDecisions: [],
    error: null,
  };
  const controlState = safeNotesState.control?.state || null;

  // Decisoes pendentes (P-E / D-22) — estado vive no ``InputDrawerV3``
  // (``useInputDecisionsV3``) e e propagado via prop ``decisionsState``
  // (antes a aba "Mensagens" tambem consumia esse estado; ela foi
  // removida na rev. 2026-04-29 e a perguntas pendentes agora vivem
  // exclusivamente aqui no NowPane). Fallback defensivo para quando o
  // pane for usado sem o drawer (storybook, testes).
  const safeDecisionsState = decisionsState || {
    decisions: [],
    loading: false,
    error: null,
    refresh: () => {},
  };
  const pendingDecisions = safeDecisionsState.decisions || [];
  const handleDecisionAnswered = () => {
    // Recarrega a lista — a decisao respondida some; se o emissor criar
    // novas decisoes decorrentes da resposta, aparecem aqui no proximo
    // tick do reconciler + refetch manual ao sair/voltar do drawer.
    safeDecisionsState.refresh();
  };

  // Autor do upload REAL — vem da state_params via adapter.
  // ``input.uploaded_by`` e preferido (estrutura completa com
  // ``is_current_user``); fallback para ``updated_by_username``
  // (nome simples, mesmo contrato com o tooltip do card). NUNCA
  // caimos no responsavel — se o backend nao registrou quem subiu,
  // o timeline mostra "Arquivo recebido" sem atribuicao.
  // Fallback ``notesState.control.uploaded_by_name`` cobre o caso de
  // input PERMANENT (yearly / fixed) aberto em mes que nao recebeu
  // upload direto: o ``input.uploaded_by`` reflete so o mes corrente
  // (e e' null nesse caso), mas o backend redireciona o ``control``
  // para a entrega real e expoe o uploader ali. Sem este fallback a
  // timeline ficaria "Arquivo recebido" sem atribuicao em fev/mar
  // mesmo quando a entrega real foi feita por alguem identificado.
  const uploaderName =
    input?.uploaded_by?.name ||
    input?.updated_by_username ||
    notesState?.control?.uploaded_by_name ||
    null;

  // Natureza permanente (singleton / yearly / carry_forward /
  // monthly+reuse_auto) altera o texto do "periodo aberto" e o rotulo
  // do estagio de envio. O nome ``isPermanentNature`` substitui o
  // antigo ``isSemiFixed`` (rev. 2026-05-28 — ver migration 0035 de
  // remocao de ``semi_fixed``); o conceito e' derivado UNICAMENTE do
  // flag ``is_permanent_for_month`` do payload.
  //
  // As leituras de ``input.nature === 'semifixed'`` e
  // ``input.source === 'auto_generated'`` foram removidas (rev.
  // 2026-06-02): no V3 o backend nao emite ``nature``/``source`` no
  // descritor do input (resquicio do V2), entao eram sempre falsas.
  const isPermanentNature = !!input?.is_permanent_for_month;

  // Estado "primeira carga": ainda nao temos nem notas nem control.
  // Com o ``useInputNotesV3`` no nivel do drawer, ``loading`` so e
  // ``true`` na PRIMEIRA carga (antes de ter qualquer dado). Trocar de
  // aba e voltar NAO reseta o estado — o hook vive no drawer — entao
  // ``isFirstLoad`` fica ``false`` apos o primeiro fetch. Com isso,
  // o spinner so aparece uma vez por abertura do drawer.
  const isFirstLoad =
    safeNotesState.loading && !safeNotesState.control && !safeNotesState.error;

  const runtimeInput = drawerDetails?.runtimeInput || null;
  const isRuntimeForCurrentInput =
    runtimeInput && runtimeInput.meta_name === input?.meta_name;
  const runtimeCrossLinking = isRuntimeForCurrentInput
    ? runtimeInput.cross_linking
    : input?.cross_linking;
  // Onda 3.4: ``attention_label`` do runtime para detectar
  // ``rejected_by_xlink`` (D-22 ``reject_input`` respondida). Garda
  // contra leak entre inputs durante refresh do drawer.
  const runtimeAttentionLabel = isRuntimeForCurrentInput
    ? runtimeInput.attention_label || null
    : input?.attention_label || null;
  const xlState = runtimeCrossLinking?.state || null;
  const xlCount =
    typeof runtimeCrossLinking?.count === "number"
      ? runtimeCrossLinking.count
      : null;
  // ``data_source`` (1o tracinho do StagePips) e' a fonte canonica do
  // estado de envio. Lemos direto do adapter — mesmo dado consumido
  // pelo InputBoxV3 — para garantir paridade visual entre o card e o
  // dash do input.
  const dataSource = input?.stages?.data_source || null;
  const isWaived = !!input?.is_waived;
  const stages = useMemo(
    () =>
      buildStages({
        controlState,
        isUploading,
        busyAction,
        hasNotesError: !!safeNotesState.error,
        xlState,
        xlCount,
        dataSource,
        isPermanent: isPermanentNature,
        isWaived,
        attentionLabel: runtimeAttentionLabel,
        t,
      }),
    [
      controlState,
      isUploading,
      busyAction,
      safeNotesState.error,
      xlState,
      xlCount,
      dataSource,
      isPermanentNature,
      isWaived,
      runtimeAttentionLabel,
      lang,
    ],
  );
  // Backend embute em ``cross_linking.clean_completed_names`` os
  // display_names dos xlinks que rodaram limpos no mes. Quando vem
  // ``null``/ausente, defaultamos para lista vazia para nao ter que
  // ramificar la dentro de ``buildTimeline``.
  const cleanCompletedNames = useMemo(
    () =>
      Array.isArray(runtimeCrossLinking?.clean_completed_names)
        ? runtimeCrossLinking.clean_completed_names
        : [],
    [runtimeCrossLinking?.clean_completed_names],
  );

  // ``crossLinksByMeta``: indexa os cross-links ja entregues pelo
  // ``drawerDetails`` por ``meta_name``. Mesma fonte da aba
  // "Cruzamentos" — sem fetch paralelo, sem enrichment server-side
  // por nota. As notas CSL trazem ``cross_link_meta_name`` (FK
  // resolvida no ``_serialize_note``) que casamos aqui para exibir
  // o ``display_name`` legivel nas caixas da timeline.
  const crossLinksByMeta = React.useMemo(() => {
    const list = drawerDetails?.crossLinks || [];
    const map = new Map();
    for (const xl of list) {
      if (xl && xl.meta_name) map.set(xl.meta_name, xl);
    }
    return map;
  }, [drawerDetails?.crossLinks]);

  const waivedByName =
    input?.waived_by?.name || input?.waived_by?.email || null;
  const timeline = useMemo(
    () =>
      buildTimeline({
        t,
        control: safeNotesState.control,
        notes: safeNotesState.notes,
        answeredDecisions: safeNotesState.answeredDecisions || [],
        isUploading,
        busyAction,
        uploadProgress,
        notesLoading: safeNotesState.loading,
        notesError: safeNotesState.error,
        uploaderName,
        isPermanentNature,
        isWaived,
        waivedAt: input?.waived_at || null,
        waivedByName,
        lang,
        xlState,
        xlCount,
        cleanCompletedNames,
        crossLinksByMeta,
      }),
    [
      t,
      safeNotesState,
      isUploading,
      busyAction,
      uploadProgress,
      uploaderName,
      isPermanentNature,
      isWaived,
      input?.waived_at,
      waivedByName,
      lang,
      xlState,
      xlCount,
      cleanCompletedNames,
      crossLinksByMeta,
    ],
  );

  if (isFirstLoad) {
    return (
      <DrawerPaneLoadingLayout
        variant="input"
        scrollRef={wheelRef}
        overlayScrollClass={overlayScrollClasses.overlayScroll}
        title={t("modeling_inputs_v3.now_pane.loading_title")}
        message={t("modeling_inputs_v3.now_pane.loading_message")}
      />
    );
  }

  // Tag "Acao necessaria" no topo do pane: aparece SOMENTE quando ha
  // de fato algo bloqueando o avanco do consultor — ou (a) uma
  // pergunta pendente (precisa responder), ou (b) o arquivo foi
  // rejeitado pelo pipeline (precisa reenviar), ou (c) cruzamento
  // critico bloqueou o avanco (precisa ajustar o dado ou aceitar a
  // divergencia via decisao). Outros estados ("warning") nao bloqueiam
  // fluxo, entao a tag fica fora.
  //
  // Onda 1.9 (mai/2026): incluimos ``xlState in (error, system_error)``
  // no calculo de ``isBlocked``. Antes a tag dependia exclusivamente
  // de notas STR severity=3 ou ``controlState==='blocked'`` — ambos
  // sinais de PARSE. Cruzamento critico nao caia em nenhum dos dois
  // (control_state continua 'success' quando o parse passou OK), entao
  // a tag de acao nunca aparecia para esse caso e o usuario nao
  // entendia que precisava agir.
  const isBlockedByCrosslink =
    xlState === "error" || xlState === "system_error";
  const isBlockedBySystemError =
    controlState === "system_error" || xlState === "system_error";
  // ``controlState`` e' a fonte de verdade do parse. Notas STR antigas
  // podem sobreviver por alguns ms apos Reprocessar; elas nao devem
  // manter a tag "Reenvio necessario" quando o control ja voltou para
  // ``uploaded``/``processing``.
  const PARSE_BLOCKED_STATES = new Set(["blocked", "error", "system_error"]);
  const isBlockedByParse = PARSE_BLOCKED_STATES.has(controlState);
  const isBlocked = isBlockedByParse || isBlockedByCrosslink;
  const hasPendingAnswer = pendingDecisions.length > 0;
  const actionLabel = (() => {
    if (isBlockedBySystemError)
      return t("modeling_inputs_v3.now_pane.action_integration_support");
    if (hasPendingAnswer && isBlocked)
      return t("modeling_inputs_v3.now_pane.action_answer_and_fix");
    if (hasPendingAnswer)
      return t("modeling_inputs_v3.now_pane.action_answer_required");
    if (isBlockedByParse)
      return t("modeling_inputs_v3.now_pane.action_resubmit");
    if (isBlockedByCrosslink)
      return t("modeling_inputs_v3.now_pane.action_fix_crosslink");
    return null;
  })();

  return (
    <div
      ref={wheelRef}
      className={`${classes.paneScroll} ${overlayScrollClasses.overlayScroll}`}
    >
      {safeNotesState.error && (
        <PaneErrorBanner
          title={t("modeling_inputs_v3.now_pane.error_load_title")}
          message={t("modeling_inputs_v3.now_pane.error_load_message")}
          detail={
            safeNotesState.error?.response?.data?.error_code ||
            safeNotesState.error?.message ||
            null
          }
          onRetry={
            typeof safeNotesState.refresh === "function"
              ? safeNotesState.refresh
              : null
          }
          retrying={safeNotesState.loading}
        />
      )}
      {!!input?.optional &&
        (() => {
          const hasDeliveredHere = !!input?.updated_in;
          const titleText = input?.is_waived
            ? t("modeling_inputs_v3.now_pane.optional_concluded_title")
            : t("modeling_inputs_v3.now_pane.optional_input_title");
          let subtitleText;
          if (input?.is_waived) {
            subtitleText = t("modeling_inputs_v3.now_pane.optional_waived_sub");
          } else if (hasDeliveredHere) {
            subtitleText = t(
              "modeling_inputs_v3.now_pane.optional_delivered_sub",
            );
          } else {
            subtitleText = t(
              "modeling_inputs_v3.now_pane.optional_not_delivered_sub",
            );
          }
          return (
            <div className={classes.optionalInputTag} role="status">
              <InfoOutlined className={classes.optionalInputTagIcon} />
              <span className={classes.optionalInputTagText}>
                {titleText}
                <span className={classes.optionalInputTagSubtitle}>
                  {subtitleText}
                </span>
              </span>
            </div>
          );
        })()}
      {actionLabel && (
        <div className={classes.actionRequiredTag} role="status">
          <ErrorOutline className={classes.actionRequiredTagIcon} />
          <span>{actionLabel}</span>
        </div>
      )}
      {pendingDecisions.length > 0 && (
        <DecisionsCard
          decisions={pendingDecisions}
          classes={classes}
          onAnswered={handleDecisionAnswered}
          onSubmittingChange={onDecisionSubmittingChange}
        />
      )}

      <div
        className={classes.section}
        style={
          isWaivePending
            ? {
                position: "relative",
                pointerEvents: "none",
                opacity: 0.45,
                transition: "opacity 160ms ease",
              }
            : { transition: "opacity 160ms ease" }
        }
        aria-busy={!!isWaivePending}
      >
        <div className={classes.sectionTitle}>
          {t("modeling_inputs_v3.now_pane.processing_status_title")}
        </div>
        <div className={classes.stages}>
          {stages.map((s) => (
            <StageCard key={s.id} stage={s} classes={classes} />
          ))}
        </div>
      </div>

      <div
        className={classes.section}
        style={
          isWaivePending
            ? {
                position: "relative",
                pointerEvents: "none",
                opacity: 0.45,
                transition: "opacity 160ms ease",
              }
            : { transition: "opacity 160ms ease" }
        }
        aria-busy={!!isWaivePending}
      >
        <div className={classes.sectionTitle}>
          {t("modeling_inputs_v3.now_pane.what_happened_title")}
          <span className={classes.sectionHint}>
            {t("modeling_inputs_v3.now_pane.what_happened_hint")}
          </span>
        </div>
        <div className={classes.timeline}>
          {timeline.map((ev) => {
            const Icon = ev.icon;
            const isPending = ev.kind === "pending";
            const kindCls =
              ev.kind === "ok"
                ? classes.tlItemOk
                : ev.kind === "warn"
                  ? classes.tlItemWarn
                  : ev.kind === "err"
                    ? classes.tlItemErr
                    : ev.kind === "action"
                      ? classes.tlItemAction
                      : ev.kind === "note"
                        ? classes.tlItemNote
                        : isPending
                          ? classes.tlItemPending
                          : classes.tlItemInfo;
            const terminalCls = ev.terminal ? ` ${classes.tlItemTerminal}` : "";
            return (
              <div
                key={ev.id}
                className={`${classes.tlItem} ${kindCls} ${classes.tlItemEntering}${terminalCls}`}
              >
                <div className={classes.tlDot}>
                  {isPending ? (
                    <span className={classes.tlSpinnerDot} />
                  ) : (
                    Icon && <Icon />
                  )}
                </div>
                <div className={classes.tlBody}>
                  <div className={classes.tlTime}>{ev.time}</div>
                  <div
                    className={`${classes.tlTitle} ${
                      ev.muted ? classes.tlTitleMuted : ""
                    }`}
                  >
                    {ev.title}
                  </div>
                  {ev.sub && <div className={classes.tlSub}>{ev.sub}</div>}
                  {/* A-014: botao "Baixar" embutido no item de
                      upload. So renderiza para o evento "Arquivo
                      recebido" (id="upload") e quando ha pelo menos
                      uma opcao de download disponivel (ja filtrada
                      pelo drawer via ``deriveDownloadOptions``). */}
                  {ev.id === "upload" && safeDownloadOptions.length > 0 && (
                    <div className={classes.tlInlineActions}>
                      <button
                        ref={downloadBtnRef}
                        type="button"
                        className={`${classes.btn} ${classes.btnGhost} ${classes.btnSmall}`}
                        onClick={handleToggleDownload}
                        disabled={!canDownload}
                        title={
                          isDownloading
                            ? t(
                                "modeling_inputs_v3.now_pane.download_in_progress",
                              )
                            : !canDownload
                              ? t(
                                  "modeling_inputs_v3.now_pane.no_file_for_download",
                                )
                              : undefined
                        }
                      >
                        {isDownloading ? (
                          <CircularProgress
                            size={14}
                            thickness={5}
                            style={{ color: clientColor }}
                          />
                        ) : (
                          <GetApp />
                        )}
                        {isDownloading
                          ? t("modeling_inputs_v3.now_pane.downloading")
                          : t("modeling_inputs_v3.now_pane.download")}
                        {!isDownloading && <ArrowDropDown />}
                      </button>
                      <DownloadMenu
                        anchorEl={downloadAnchor}
                        open={Boolean(downloadAnchor)}
                        onClose={handleCloseDownload}
                        options={safeDownloadOptions}
                        onPick={handlePickDownload}
                      />
                    </div>
                  )}
                  {/* Sub-cards de notas STR (sev=1 e sev=2),
                      agrupados por severidade. Cada grupo e
                      precedido por um cabecalho discreto
                      ("Informativos" / "Pontos de atencao") com a
                      cor da severidade — assim o consultor enxerga
                      direto o "que e o que" sem ter que decorar o
                      mapeamento azul/amarelo. Mesma estrutura
                      adotada nos cruzamentos abaixo. */}
                  {Array.isArray(ev.noteSubItems) &&
                    ev.noteSubItems.length > 0 && (
                      <div className={classes.tlCrossList}>
                        {SEVERITY_GROUPS.map((g) => {
                          const itemsInGroup = ev.noteSubItems.filter(
                            (s) => s.sevKind === g.sevKind,
                          );
                          if (itemsInGroup.length === 0) return null;
                          const cardCls =
                            g.sevKind === "err"
                              ? classes.tlCrossCardErr
                              : g.sevKind === "warn"
                                ? classes.tlCrossCardWarn
                                : classes.tlCrossCardNote;
                          return (
                            <React.Fragment key={`note-grp:${g.sevKind}`}>
                              <div
                                className={`${classes.tlSeverityGroupLabel} ${classes[g.labelClsKey]}`}
                              >
                                {t(g.labelKey)}
                              </div>
                              {itemsInGroup.map((sub) => (
                                <div
                                  key={sub.id}
                                  className={`${classes.tlCrossCard} ${cardCls}`}
                                >
                                  <div className={classes.tlCrossTitle}>
                                    {sub.title}
                                  </div>
                                  {sub.body && (
                                    <div className={classes.tlCrossBody}>
                                      {sub.body}
                                    </div>
                                  )}
                                  <MarkAnalyzedControl
                                    sub={sub}
                                    override={optimisticMarks[sub.noteId]}
                                    onMark={handleMarkNote}
                                    pending={!!markingNotes[sub.noteId]}
                                  />
                                </div>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                  {/* Sub-cards de cruzamento, agrupados por
                      severidade — mesmo padrao adotado nas notas
                      STR acima. ATENCAO: sev=1 caia em
                      ``tlCrossCardOk`` (verde) ate a rodada
                      2026-04-27; agora vai para ``tlCrossCardNote``
                      (azul / informativo). Verde fica reservado a
                      "concluido sem apontamentos" — caixas verdes
                      eram enganosas pra um achado real. */}
                  {Array.isArray(ev.crossSubItems) &&
                    ev.crossSubItems.length > 0 && (
                      <div className={classes.tlCrossList}>
                        {SEVERITY_GROUPS.map((g) => {
                          const itemsInGroup = ev.crossSubItems
                            .filter((s) => s.sevKind === g.sevKind)
                            // Ordenacao dentro do grupo: pior
                            // qualidade primeiro (menor ratio = mais
                            // crítico). Notas sem ``qualityRatio``
                            // ficam no fim — sem dado para hierarquizar
                            // joga para o final do grupo (ainda dentro
                            // da sua faixa de criticidade).
                            .slice()
                            .sort((a, b) => {
                              const qa =
                                typeof a.qualityRatio === "number"
                                  ? a.qualityRatio
                                  : Number.POSITIVE_INFINITY;
                              const qb =
                                typeof b.qualityRatio === "number"
                                  ? b.qualityRatio
                                  : Number.POSITIVE_INFINITY;
                              return qa - qb;
                            });
                          if (itemsInGroup.length === 0) return null;
                          const cardCls =
                            g.sevKind === "err"
                              ? classes.tlCrossCardErr
                              : g.sevKind === "warn"
                                ? classes.tlCrossCardWarn
                                : classes.tlCrossCardNote;
                          // Cor do badge "% qualidade" segue a cor
                          // do grupo — usa a mesma classe ja
                          // existente para o cabecalho de grupo
                          // (``tlSeverityGroupLabel*``), que define
                          // ``color: infoColor[1] | warningColor[1]
                          // | dangerColor[2]``.
                          const qualityColorCls = classes[g.labelClsKey];
                          return (
                            <React.Fragment key={`csl-grp:${g.sevKind}`}>
                              <div
                                className={`${classes.tlSeverityGroupLabel} ${classes[g.labelClsKey]}`}
                              >
                                {t(g.labelKey)}
                              </div>
                              {itemsInGroup.map((sub) => (
                                <div
                                  key={sub.id}
                                  className={`${classes.tlCrossCard} ${cardCls}`}
                                >
                                  <div className={classes.tlCrossHeader}>
                                    <div className={classes.tlCrossTitle}>
                                      {sub.title}
                                    </div>
                                    {typeof sub.qualityRatio === "number" && (
                                      <div
                                        className={`${classes.tlCrossQuality} ${qualityColorCls}`}
                                        title={t(
                                          "modeling_inputs_v3.now_pane.quality_index_title",
                                        )}
                                      >
                                        {(
                                          sub.qualityRatio * 100
                                        ).toLocaleString(lang, {
                                          minimumFractionDigits: 1,
                                          maximumFractionDigits: 1,
                                        })}
                                        %
                                      </div>
                                    )}
                                  </div>
                                  {sub.body && (
                                    <div className={classes.tlCrossBody}>
                                      {sub.body}
                                    </div>
                                  )}
                                  {sub.meta && sub.meta.length > 0 && (
                                    <div className={classes.tlCrossMeta}>
                                      {sub.meta.map((m, i) => (
                                        <span key={i}>{m}</span>
                                      ))}
                                    </div>
                                  )}
                                  {((Array.isArray(sub.missingSample) &&
                                    sub.missingSample.length > 0) ||
                                    sub.hasMissingBlob) && (
                                    <CrossSampleCopy
                                      sample={sub.missingSample}
                                      total={sub.missingTotal}
                                      noteId={sub.noteId}
                                      hasMissingBlob={sub.hasMissingBlob}
                                    />
                                  )}
                                  {/* Onda 3.4 (CCU Testes, mai/2026):
                                      complemento de decisao respondida
                                      anexado ao sub-card critico —
                                      fecha o ciclo "ponto critico ->
                                      decisao -> resolucao" na mesma
                                      linha visual. Aparece so quando o
                                      backend marcou ``decision_reject``
                                      no xlink correspondente. */}
                                  {sub.decisionInfo && (
                                    <div className={classes.tlCrossDecision}>
                                      {sub.decisionInfo.kind === "accept" ? (
                                        <Check
                                          className={`${classes.tlCrossDecisionIcon} ${classes.tlCrossDecisionIconUp}`}
                                        />
                                      ) : (
                                        <Clear
                                          className={`${classes.tlCrossDecisionIcon} ${classes.tlCrossDecisionIconDown}`}
                                        />
                                      )}
                                      <strong>
                                        {sub.decisionInfo.answeredBy}
                                      </strong>{" "}
                                      {sub.decisionInfo.kind === "accept" ? (
                                        <>
                                          {t(
                                            "modeling_inputs_v3.now_pane.timeline.decision_accept_verb",
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          {t(
                                            "modeling_inputs_v3.now_pane.timeline.decision_reject_verb",
                                          )}{" "}
                                          <strong>
                                            {
                                              sub.decisionInfo
                                                .rejectedDisplayName
                                            }
                                          </strong>
                                        </>
                                      )}
                                      {sub.decisionInfo.answeredAt && (
                                        <>
                                          {" "}
                                          {t(
                                            "modeling_inputs_v3.now_pane.timeline.decision_at",
                                          )}{" "}
                                          {formatDeliveredAt(
                                            sub.decisionInfo.answeredAt,
                                          )}
                                        </>
                                      )}
                                    </div>
                                  )}
                                  <MarkAnalyzedControl
                                    sub={sub}
                                    override={optimisticMarks[sub.noteId]}
                                    onMark={handleMarkNote}
                                    pending={!!markingNotes[sub.noteId]}
                                    withDivider={
                                      ((Array.isArray(sub.missingSample) &&
                                        sub.missingSample.length > 0) ||
                                        sub.hasMissingBlob) &&
                                      !sub.decisionInfo
                                    }
                                  />
                                </div>
                              ))}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                  {/* Barra de progresso do upload — so aparece em items
                      que expoem ``progress`` (numero 0..100). Renderizada
                      no lugar onde faz sentido: logo abaixo do subtexto
                      do passo "Enviando arquivo". */}
                  {typeof ev.progress === "number" && (
                    <div className={classes.tlProgressTrack}>
                      <div
                        className={classes.tlProgressFill}
                        style={{ width: `${ev.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

NowPane.propTypes = {
  input: PropTypes.object,
  isUploading: PropTypes.bool,
  busyAction: PropTypes.oneOf(["upload", "replicate", "reprocess"]),
  isDownloading: PropTypes.bool,
  downloadOptions: PropTypes.array,
  onDownload: PropTypes.func,
  notesState: PropTypes.shape({
    loading: PropTypes.bool,
    notes: PropTypes.array,
    control: PropTypes.object,
    error: PropTypes.any,
  }),
  drawerDetails: PropTypes.shape({
    crossLinks: PropTypes.array,
    runtimeInput: PropTypes.object,
  }),
  decisionsState: PropTypes.shape({
    decisions: PropTypes.array,
    loading: PropTypes.bool,
    error: PropTypes.any,
    refresh: PropTypes.func,
  }),
  isWaivePending: PropTypes.bool,
  onDecisionSubmittingChange: PropTypes.func,
  onMarkNoteVerified: PropTypes.func,
};

export default NowPane;
